import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import '../styles/Review.css';

const API_BASE =
  import.meta.env.VITE_API_BASE_URL ||
  import.meta.env.VITE_API_URL ||
  'https://api.e-invitation.online';

// ─── Star Rating Component ──────────────────────────────────────────────────
function StarRating({ label, value, onChange, required = false }) {
  const [hovered, setHovered] = useState(0);

  return (
    <div className="star-rating-group">
      {label && (
        <label className="star-rating-label">
          {label}
          {required && <span className="required-mark"> *</span>}
        </label>
      )}
      <div className="star-rating-stars" role="group" aria-label={label}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star-btn ${star <= (hovered || value) ? 'active' : ''}`}
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            aria-label={`${star} star${star !== 1 ? 's' : ''}`}
          >
            ★
          </button>
        ))}
      </div>
      {value > 0 && (
        <span className="star-rating-text">
          {['', 'Poor', 'Fair', 'Good', 'Very Good', 'Excellent'][value]}
        </span>
      )}
    </div>
  );
}

// ─── Nav Header ──────────────────────────────────────────────────────────────
function NavBar() {
  return (
    <nav className="review-nav">
      <Link to="/" className="review-nav-logo">
        <span className="review-nav-icon">✉️</span>
        <span className="review-nav-brand">e-invitation<span className="review-nav-dot">.online</span></span>
      </Link>
      <Link to="/" className="review-nav-home">← Back to Home</Link>
    </nav>
  );
}

// ─── Main Review Page ───────────────────────────────────────────────────────
export default function Review() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [status, setStatus] = useState('loading'); // loading | ready | no-token | invalid | already-submitted | expired | event-not-passed | success | error
  const [info, setInfo] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');

  // Form state
  const [rating, setRating] = useState(0);
  const [ratingDesign, setRatingDesign] = useState(0);
  const [ratingCommunication, setRatingCommunication] = useState(0);
  const [ratingValue, setRatingValue] = useState(0);
  const [comment, setComment] = useState('');
  const [charCount, setCharCount] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [fieldError, setFieldError] = useState('');

  // ── Load token info on mount ──────────────────────────────────────────────
  const loadTokenInfo = useCallback(async () => {
    if (!token) {
      setStatus('no-token');
      return;
    }

    try {
      const res = await fetch(`${API_BASE}/reviews/token/${encodeURIComponent(token)}`);
      const data = await res.json();

      if (res.status === 409) { setStatus('already-submitted'); return; }
      if (res.status === 410) { setStatus('expired'); return; }
      if (!res.ok || !data.ok) {
        setErrorMsg(data.message || 'Invalid review link.');
        setStatus('invalid');
        return;
      }

      if (!data.data.eventPassed) {
        setInfo(data.data);
        setStatus('event-not-passed');
        return;
      }

      setInfo(data.data);
      setStatus('ready');
    } catch {
      setErrorMsg('Unable to load review information. Please try again later.');
      setStatus('error');
    }
  }, [token]);

  useEffect(() => { loadTokenInfo(); }, [loadTokenInfo]);

  // ── Comment change ─────────────────────────────────────────────────────────
  function handleCommentChange(e) {
    const val = e.target.value;
    setComment(val);
    setCharCount(val.length);
  }

  // ── Submit ─────────────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    if (submitting) return;
    setFieldError('');

    if (rating === 0) {
      setFieldError('Please select an overall star rating.');
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/reviews/token/${encodeURIComponent(token)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          rating,
          ratingDesign: ratingDesign || undefined,
          ratingCommunication: ratingCommunication || undefined,
          ratingValue: ratingValue || undefined,
          comment: comment.trim() || undefined
        })
      });
      const data = await res.json();

      if (!res.ok || !data.ok) {
        if (res.status === 409) { setStatus('already-submitted'); return; }
        if (res.status === 410) { setStatus('expired'); return; }
        setFieldError(data.message || 'Something went wrong. Please try again.');
        return;
      }

      setStatus('success');
    } catch {
      setFieldError('Network error. Please check your connection and try again.');
    } finally {
      setSubmitting(false);
    }
  }

  // ─── Render states ──────────────────────────────────────────────────────────
  if (status === 'loading') {
    return (
      <div className="review-page">
        <NavBar />
        <div className="review-card review-card--center">
          <div className="review-spinner" aria-label="Loading" />
          <p>Loading your review form…</p>
        </div>
      </div>
    );
  }

  if (status === 'no-token') {
    return (
      <div className="review-page">
        <NavBar />
        <div className="review-card review-card--center">
          <div className="review-icon">🔗</div>
          <h2>Invalid Review Link</h2>
          <p>No review token was found. Please use the link sent to your email.</p>
          <Link to="/" className="review-home-btn">Back to Home</Link>
        </div>
      </div>
    );
  }

  if (status === 'invalid') {
    return (
      <div className="review-page">
        <NavBar />
        <div className="review-card review-card--center">
          <div className="review-icon">❌</div>
          <h2>Link Not Found</h2>
          <p>{errorMsg}</p>
          <Link to="/" className="review-home-btn">Back to Home</Link>
        </div>
      </div>
    );
  }

  if (status === 'already-submitted') {
    return (
      <div className="review-page">
        <NavBar />
        <div className="review-card review-card--center">
          <div className="review-icon">✅</div>
          <h2>Review Already Submitted</h2>
          <p>You have already left a review for this booking. Thank you!</p>
          <Link to="/" className="review-home-btn">Back to Home</Link>
        </div>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div className="review-page">
        <NavBar />
        <div className="review-card review-card--center">
          <div className="review-icon">⏰</div>
          <h2>Review Link Expired</h2>
          <p>This review link has expired (30-day window after your event). Contact us if you still wish to leave feedback.</p>
          <Link to="/contact" className="review-home-btn">Contact Us</Link>
        </div>
      </div>
    );
  }

  if (status === 'event-not-passed') {
    const eventDate = info?.eventDate
      ? new Date(info.eventDate).toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })
      : 'your event date';
    return (
      <div className="review-page">
        <NavBar />
        <div className="review-card review-card--center">
          <div className="review-icon">📅</div>
          <h2>Your Event Hasn't Happened Yet</h2>
          <p>You can leave a review after your event on <strong>{eventDate}</strong>. Come back then!</p>
          <Link to="/" className="review-home-btn">Back to Home</Link>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="review-page">
        <NavBar />
        <div className="review-card review-card--center">
          <div className="review-icon">⚠️</div>
          <h2>Something Went Wrong</h2>
          <p>{errorMsg}</p>
          <Link to="/" className="review-home-btn">Back to Home</Link>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="review-page">
        <NavBar />
        <div className="review-card review-card--center review-card--success">
          <div className="review-icon">🎉</div>
          <h2>Thank You for Your Review!</h2>
          <p>Your feedback means a lot to us. It helps us create even better invitations for future celebrations.</p>
          <Link to="/" className="review-home-btn">Back to Home</Link>
        </div>
      </div>
    );
  }

  // ─── Ready — Show form ──────────────────────────────────────────────────────
  const eventDateFormatted = info?.eventDate
    ? new Date(info.eventDate).toLocaleDateString('en-PH', { year: 'numeric', month: 'long', day: 'numeric' })
    : 'your event date';

  return (
    <div className="review-page">
      <NavBar />
      <div className="review-card">
        {/* Header */}
        <div className="review-header">
          <div className="review-logo">⭐</div>
          <h1 className="review-title">Leave a Review</h1>
          <p className="review-subtitle">
            Hi <strong>{info?.reviewerName}</strong>! How was your{' '}
            <strong>{info?.eventType || 'event'}</strong> experience on {eventDateFormatted}?
          </p>
          {info?.templateName && (
            <p className="review-template-badge">Design: {info.templateName}</p>
          )}
        </div>

        {/* Form */}
        <form className="review-form" onSubmit={handleSubmit} noValidate>

          {/* Overall Rating */}
          <StarRating
            label="Overall Experience"
            value={rating}
            onChange={setRating}
            required
          />

          {/* Aspect Ratings */}
          <div className="review-aspects">
            <p className="review-aspects-title">Rate specific aspects <span className="optional-tag">(optional)</span></p>
            <div className="review-aspects-grid">
              <StarRating
                label="Design Quality"
                value={ratingDesign}
                onChange={setRatingDesign}
              />
              <StarRating
                label="Communication"
                value={ratingCommunication}
                onChange={setRatingCommunication}
              />
              <StarRating
                label="Value for Money"
                value={ratingValue}
                onChange={setRatingValue}
              />
            </div>
          </div>

          {/* Comment */}
          <div className="review-field">
            <label htmlFor="review-comment">
              Share your experience <span className="optional-tag">(optional)</span>
            </label>
            <textarea
              id="review-comment"
              className="review-textarea"
              placeholder="Tell us what you loved, what could be improved, or anything else you'd like to share…"
              value={comment}
              onChange={handleCommentChange}
              rows={5}
              maxLength={1000}
            />
            <div className="review-char-count">{charCount} / 1000</div>
          </div>

          {/* Field Error */}
          {fieldError && (
            <div className="review-field-error" role="alert">{fieldError}</div>
          )}

          {/* Submit */}
          <button
            type="submit"
            className="review-submit-btn"
            disabled={submitting}
          >
            {submitting ? 'Submitting…' : 'Submit Review'}
          </button>

          <p className="review-privacy-note">
            Your review may be displayed publicly on our website after moderation.
          </p>
        </form>
      </div>
    </div>
  );
}
